import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Alert, Box, Button, Chip, Spinner, Typography } from '@glplus/glpweb-dsl';
import { ContinueOnPhone } from '../components/ContinueOnPhone';
import { RunnerAction } from '../components/RunnerAction';
import { RunnerPageHeading } from '../components/RunnerPageHeading';
import { Panel } from '../components/Panel';
import { runnerApi, RunnerApiError, type PickupRun, type RunStatus } from '../lib/runnerApiClient';
import { RUN_STATUS_STYLE, prettyInstant, prettyRunDate, prettyTime, ymdOffset } from '../lib/runs';

/**
 * §13 step 9a — "Your runs", deck §6. The record of every pickup run:
 * upcoming and recent, each opening to its stops and deliveries.
 *
 * 🔒 READ-ONLY on the web, by ruling (2026-06-21: the native app is the sole
 * field tool). Starting a run, arriving at a stop, picking up and confirming a
 * delivery are things a Runner does with a phone in a car park, and the API
 * client's field calls exist for that app. This screen renders the record and
 * says where the actions live rather than offering buttons that would let
 * someone mark a stop "picked up" from a desk.
 *
 * 🪤 The deck's proof-of-delivery upload tiles (receipt + delivered photo on a
 * completed run) are DESIGN ONLY — the runs API has no upload endpoint for
 * them, and the receipt is a settlement input gated on Stripe Issuing (9b).
 * Not drawn here until the backend exists; a dead upload tile is worse than
 * none.
 *
 * 🛑 A failed load renders the failure, never "no runs". A Runner with a full
 * week must not be told it is empty because a request did not complete.
 */

const ORANGE = '#F67A1A';
const NAVY = '#343F5B';

const LOOKBACK_DAYS = 30;
const LOOKAHEAD_DAYS = 14;

export default function Runs({ canContinue = true }: { canContinue?: boolean }) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language || 'en';
  const [runs, setRuns] = useState<PickupRun[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [params] = useSearchParams();
  const [open, setOpen] = useState<string | null>(params.get('run'));
  const [phoneRun, setPhoneRun] = useState<string | null>(null);
  const [refresh, setRefresh] = useState(0);
  const requestedView = params.get('view');
  const view = requestedView === 'today' || requestedView === 'upcoming' ? requestedView : undefined;
  const requestedRun = params.get('run');
  const [calendarToday, setCalendarToday] = useState<string | null>(null);
  useEffect(() => { setOpen(requestedRun); }, [requestedRun]);

  useEffect(() => {
    let cancelled = false;
    setRuns(null); setError(null);
    void (async () => {
      try {
        let page = await runnerApi.runs(view ? { view, limit: 100 } : { from: ymdOffset(-LOOKBACK_DAYS), to: ymdOffset(LOOKAHEAD_DAYS), limit: 100 });
        const all = [...page.runs];
        const serverToday = page.calendar?.today;
        while (page.hasMore) {
          if (!page.runs.length || all.length >= 100000) throw new Error(t('runs.loadFailed'));
          page = await runnerApi.runs(view ? { view, limit: 100, offset: all.length } : { from: ymdOffset(-LOOKBACK_DAYS), to: ymdOffset(LOOKAHEAD_DAYS), limit: 100, offset: all.length });
          all.push(...page.runs);
        }
        if (requestedRun && !all.some(run => run.id === requestedRun)) all.push((await runnerApi.run(requestedRun)).run);
        if (!cancelled) { setRuns(all); setCalendarToday(serverToday ?? null); }
      } catch (e) { if (!cancelled) setError(e instanceof RunnerApiError ? e.message : t('runs.loadFailed')); }
    })();
    return () => {
      cancelled = true;
    };
  }, [t, view, requestedRun, refresh]);

  const today = calendarToday ?? ymdOffset(0);
  // The API returns ascending by date. Upcoming reads forward from today;
  // recent reads backward from yesterday, most recent first.
  const upcoming = useMemo(() => (runs ?? []).filter((r) => r.runDate >= today), [runs, today]);
  const recent = useMemo(() => (runs ?? []).filter((r) => r.runDate < today).slice().reverse(), [runs, today]);

  return (
    <Box data-testid="page-runs">
      <ContinueOnPhone runId={phoneRun} onClose={() => setPhoneRun(null)} />
      <RunnerPageHeading title={view === 'today' ? (locale.startsWith('es') ? 'Hoy' : 'Today') : view === 'upcoming' ? (locale.startsWith('es') ? 'Próximas rutas' : 'Upcoming') : t('runs.title')} subtitle={t('runs.subtitle')} onRefresh={() => setRefresh(value => value + 1)} refreshing={runs === null && !error} />

      {error ? (
        <Alert severity="error" data-testid="runs-error">{error}</Alert>
      ) : runs === null ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', pt: 6 }}>
          <Spinner />
        </Box>
      ) : (
        <Box data-testid="runs-loaded" data-run-count={runs.length} sx={{ display: 'grid', gap: 2.5 }}>
          <Panel title={t('runs.upcomingTitle')} accent={ORANGE} dataTestId="runs-upcoming">
            {upcoming.length === 0 ? (
              <Typography data-testid="runs-upcoming-empty" sx={{ color: '#4b5563', fontSize: '14px' }}>{t('runs.upcomingEmpty')}</Typography>
            ) : (
              <RunList runs={upcoming} locale={locale} open={open} setOpen={setOpen} onContinue={canContinue ? setPhoneRun : undefined} />
            )}
          </Panel>
          <Panel title={t('runs.recentTitle', { days: LOOKBACK_DAYS })} accent={NAVY} dataTestId="runs-recent">
            {recent.length === 0 ? (
              <Typography data-testid="runs-recent-empty" sx={{ color: '#4b5563', fontSize: '14px' }}>{t('runs.recentEmpty', { days: LOOKBACK_DAYS })}</Typography>
            ) : (
              <RunList runs={recent} locale={locale} open={open} setOpen={setOpen} onContinue={canContinue ? setPhoneRun : undefined} />
            )}
          </Panel>
        </Box>
      )}
    </Box>
  );
}

function RunList({ runs, locale, open, setOpen, onContinue }: { runs: PickupRun[]; locale: string; open: string | null; setOpen: (id: string | null) => void; onContinue?: (id: string) => void }) {
  return (
    <Box sx={{ display: 'grid', gap: 1.25 }}>
      {runs.map((r) => (
        <RunRow key={r.id} run={r} locale={locale} expanded={open === r.id} onToggle={() => setOpen(open === r.id ? null : r.id)} onContinue={onContinue} />
      ))}
    </Box>
  );
}

function RunRow({ run, locale, expanded, onToggle, onContinue }: { run: PickupRun; locale: string; expanded: boolean; onToggle: () => void; onContinue?: (id: string) => void }) {
  const { t } = useTranslation();
  const style = RUN_STATUS_STYLE[run.status] ?? RUN_STATUS_STYLE.open;
  return (
    <Box data-testid={`run-row-${run.id}`} data-status={run.status} sx={{ border: '1px solid #e5e7eb', borderRadius: 2, px: 1.75, py: 1.25 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.5, flexWrap: 'wrap' }}>
        <Box>
          <Typography sx={{ fontWeight: 700, fontSize: '15px', color: '#1f2937' }}>
            {prettyRunDate(run.runDate, locale)} · {prettyTime(run.deliveryWindowStart, locale)}–{prettyTime(run.deliveryWindowEnd, locale)}
          </Typography>
          <Typography sx={{ color: '#5f6b7a', fontSize: '13px' }}>
            {run.zone} · {run.orderCount === 1 ? t('runs.oneOrder') : t('runs.orders', { count: run.orderCount })}
            {run.status === 'cancelled' && run.cancelReason ? ` · ${run.cancelReason}` : ''}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Chip label={t(`runs.status.${run.status as RunStatus}`)} size="small" backgroundColor={style.bg} textColor={style.fg} fontWeight={700} />
          <Button ariaLabel={expanded ? t('runs.hide') : t('runs.details')} dataTestId={`run-toggle-${run.id}`} buttonText={expanded ? t('runs.hide') : t('runs.details')} variant="text" size="small" onClick={onToggle} />
        </Box>
      </Box>
      {onContinue && ['in_prep', 'pickup_in_progress', 'out_for_delivery'].includes(run.status) && <Box sx={{ mt: 2 }}><RunnerAction topic="attention" buttonText={t('runnerHome.continueRun')} ariaLabel={t('runnerHome.continueRun')} onClick={() => onContinue(run.id)} /></Box>}
      {expanded && <RunDetail id={run.id} locale={locale} />}
    </Box>
  );
}

function RunDetail({ id, locale }: { id: string; locale: string }) {
  const { t } = useTranslation();
  const [run, setRun] = useState<PickupRun | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setError(null);
      setRun((await runnerApi.run(id)).run);
    } catch (e) {
      setError(e instanceof RunnerApiError ? e.message : t('runs.detailFailed'));
    }
  }, [id, t]);

  useEffect(() => {
    void load();
  }, [load]);

  if (error) {
    return (
      <Box sx={{ mt: 1.5 }}>
        <Alert severity="error" data-testid={`run-detail-error-${id}`}>{error}</Alert>
      </Box>
    );
  }
  if (!run) {
    return (
      <Box sx={{ mt: 1.5 }}>
        <Spinner size={20} />
      </Box>
    );
  }
  const stops = run.stops ?? [];
  const orders = run.orders ?? [];
  return (
    <Box data-testid={`run-detail-${id}`} data-stop-count={stops.length} data-order-count={orders.length} sx={{ mt: 1.5, pt: 1.5, borderTop: '1px solid #f1f5f9', display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
      <Box>
        <Typography sx={{ fontWeight: 700, fontSize: '13px', color: NAVY, mb: 0.75 }}>{t('runs.stopsTitle')}</Typography>
        {stops.length === 0 ? (
          <Typography data-testid="run-detail-no-stops" sx={{ color: '#5f6b7a', fontSize: '13px' }}>{t('runs.noStops')}</Typography>
        ) : (
          <Box sx={{ display: 'grid', gap: 0.75 }}>
            {stops.map((s) => (
              <Box key={s.id} data-testid={`run-stop-${s.id}`}>
                <Typography sx={{ color: '#1f2937', fontSize: '14px', fontWeight: 600 }}>
                  {t('runs.stop', { n: s.sequence })} · {s.displayName}
                  {s.banner ? ` (${s.banner})` : ''}
                </Typography>
                <Typography sx={{ color: '#5f6b7a', fontSize: '12px' }}>
                  {[
                    s.stopKind === 'chain_store' ? t('runs.chainStore') : t('runs.localVendor'),
                    s.fulfillmentMode === 'runner_picks' ? t('runs.runnerPicks') : t('runs.vendorPrepped'),
                    s.prepStatus ? t(`runs.prep.${s.prepStatus}`) : null,
                    s.arrivedAt ? t('runs.arrived', { time: prettyInstant(s.arrivedAt, locale) }) : null,
                    s.pickedUpAt ? t('runs.pickedUp', { time: prettyInstant(s.pickedUpAt, locale) }) : null,
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                </Typography>
              </Box>
            ))}
          </Box>
        )}
      </Box>
      <Box>
        <Typography sx={{ fontWeight: 700, fontSize: '13px', color: NAVY, mb: 0.75 }}>{t('runs.ordersTitle')}</Typography>
        {orders.length === 0 ? (
          <Typography data-testid="run-detail-no-orders" sx={{ color: '#5f6b7a', fontSize: '13px' }}>{t('runs.noOrders')}</Typography>
        ) : (
          <Box sx={{ display: 'grid', gap: 0.75 }}>
            {orders.map((o) => (
              <Box key={o.id} data-testid={`run-delivery-${o.id}`} data-payment-hold={o.paymentHold ?? ''}>
                <Typography sx={{ color: '#1f2937', fontSize: '14px', fontWeight: 600 }}>
                  {o.deliverySequence !== null ? `${t('runs.drop', { n: o.deliverySequence })} · ` : ''}
                  <Box component="span" sx={{ fontFamily: 'monospace', fontWeight: 500, color: NAVY }}>{o.glpo ?? o.orderId}</Box>
                </Typography>
                <Typography sx={{ color: '#5f6b7a', fontSize: '13px' }}>{o.customerDisplayName || o.customerGlpUserId}</Typography>
                <Typography sx={{ color: '#5f6b7a', fontSize: '12px' }}>
                  {[
                    t(`runs.orderStatus.${o.fulfillmentStatus}`, { defaultValue: humanise(o.fulfillmentStatus) }),
                    o.deliveredAt ? t('runs.delivered', { time: prettyInstant(o.deliveredAt, locale) }) : null,
                    o.undeliveredAt ? t(o.undeliveredReason === 'safety' ? 'runs.undeliveredSafety' : 'runs.undeliveredNobodyHome', { time: prettyInstant(o.undeliveredAt, locale) }) : null,
                    o.donatedAt ? t('runs.donated', { time: prettyInstant(o.donatedAt, locale) }) : null,
                    o.confirmedAt ? t('runs.confirmed', { time: prettyInstant(o.confirmedAt, locale) }) : null,
                    o.paymentHold === 'at_risk' && o.fulfillmentStatus !== 'cancelled' ? t('runs.atRisk', { time: o.cardFixBy ? prettyInstant(o.cardFixBy, locale) : '' }) : null,
                    o.exceptionCode ? t('runs.exception', { code: t(`runs.exceptionCode.${o.exceptionCode}`, { defaultValue: humanise(o.exceptionCode) }) }) : null,
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                </Typography>
              </Box>
            ))}
          </Box>
        )}
      </Box>
      <Typography sx={{ color: '#5f6b7a', fontSize: '12px', gridColumn: '1 / -1' }}>{t('runs.fieldNote')}</Typography>
    </Box>
  );
}

/** 'out_for_delivery' -> 'Out for delivery'. For enum values the API does not
 *  document; anything known gets a real string above. */
function humanise(v: string): string {
  const s = v.replace(/_/g, ' ');
  return s.charAt(0).toUpperCase() + s.slice(1);
}
